import React from 'react';
import {
  ClipboardCheck, Phone, MessageCircle, ArrowRight, ShieldCheck, Clock,
  GraduationCap, Briefcase, Building2, Users, MapPin, Bot
} from 'lucide-react';
import SEO from '../SEO';

const BG_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/2b41e164-3d6f-488e-a974-c4593600dbb3/images/ab108687f1a2175d5c73ce0a2d1df2c84b0c728c96abc17d8170465e0bb67a66.jpeg';
const LOGO = 'https://customer-assets.emergentagent.com/job_f317ca07-18a7-4aa4-ba98-b80b50e0c72d/artifacts/tzjfylqh_AJ%20LOGO%20BRANCA%20-%20BIMPORTAL.PNG';

const SERVICES = [
  { icon: GraduationCap, title: 'AJ | BIM Academy', desc: 'Cursos e Formações profissionais em Revit e BIM.', href: '/academy' },
  { icon: Briefcase, title: 'Consultoria BIM', desc: 'Implantação e estratégia BIM para empresas e órgãos públicos.', href: '/consultoria' },
  { icon: Building2, title: 'Projetos BIM', desc: 'Modelagem, compatibilização e projetos completos em BIM.', href: '/projetos' },
  { icon: Users, title: 'AJ | BIM Connect', desc: 'A maior rede social de profissionais BIM da América Latina.', href: '/connect' },
  { icon: MapPin, title: 'AJ | BIM Map', desc: 'Encontre e seja encontrado por profissionais e empresas BIM.', href: '/bimmap' },
  { icon: Bot, title: 'AJ | BIM IA', desc: 'Inteligência artificial especialista em BIM ao seu lado.', href: '/bim-ia' },
];

const InscricaoRecebidaPage = () => {
  return (
    <div className="relative min-h-screen bg-black" data-testid="inscricao-recebida-page">
      <SEO
        title="Inscrição recebida | AJ | BIMPORTAL"
        description="Obrigado por preencher o formulário. Recebemos a sua inscrição e a nossa equipe entrará em contato em breve."
      />

      {/* ===== HERO ===== */}
      <section className="relative flex items-center justify-center overflow-hidden min-h-[85vh]">
        <div className="absolute inset-0">
          <img src={BG_IMAGE} alt="BIMPORTAL - Serviços e Produtos BIM" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/80 to-red-950/70" />
        </div>
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-red-600 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-800 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 w-full max-w-3xl mx-auto px-6 py-16 text-center">
          <img src={LOGO} alt="AJ | BIMPORTAL" className="h-12 md:h-14 mx-auto mb-10 animate-in fade-in duration-700" />

          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-green-500/15 border-2 border-green-400/40 flex items-center justify-center animate-in zoom-in duration-500">
              <ClipboardCheck className="w-11 h-11 text-green-400" />
            </div>
          </div>

          <h1 className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight">
            Inscrição <span className="text-red-500">recebida!</span>
          </h1>

          <p className="text-base md:text-xl text-gray-200 leading-relaxed mb-4 max-w-2xl mx-auto">
            Muito obrigado por preencher o formulário e pela confiança no
            <strong className="text-white"> BIMPORTAL</strong>. Recebemos as suas informações
            com sucesso e já demos início ao seu atendimento.
          </p>
        </div>
      </section>

      {/* ===== CONTEÚDO ===== */}
      <section className="relative z-10 bg-zinc-950 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6">

          {/* Próximos passos */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 md:p-8 mb-8" data-testid="inscricao-proximos-passos">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-red-600/15 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-red-500" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-white">O que acontece agora?</h2>
            </div>
            <p className="text-gray-300 text-sm md:text-base leading-relaxed">
              A nossa equipe irá analisar as suas informações e entrará em contato o mais breve possível,
              pelo e-mail e/ou telefone informados no formulário. Fique de olho na sua caixa de entrada
              (e também na pasta de <strong className="text-white">spam ou promoções</strong>) para não
              perder nenhuma novidade.
            </p>
          </div>

          {/* Suporte / Telefone */}
          <div className="bg-gradient-to-br from-red-900/20 to-zinc-900 border border-red-500/30 rounded-2xl p-6 md:p-8 mb-12" data-testid="inscricao-suporte">
            <div className="flex items-center gap-2 text-red-400 mb-3">
              <ShieldCheck size={18} />
              <span className="text-sm font-semibold uppercase tracking-wider">Quer falar agora?</span>
            </div>
            <p className="text-gray-200 text-sm md:text-base mb-6">
              Se preferir agilizar o seu atendimento ou tiver qualquer dúvida, fale diretamente
              com a nossa equipe pelo telefone abaixo.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <a
                href="https://wa.me/5561995792667"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-full px-6 py-3 transition-colors"
                data-testid="inscricao-whatsapp"
              >
                <MessageCircle size={20} />
                WhatsApp: +55 61 99579-2667
              </a>
              <a
                href="tel:+5561995792667"
                className="inline-flex items-center justify-center gap-2 border border-white/25 text-white hover:bg-white/10 font-semibold rounded-full px-6 py-3 transition-colors"
                data-testid="inscricao-phone"
              >
                <Phone size={18} />
                Ligar agora
              </a>
            </div>
          </div>

          {/* Vitrine de produtos e serviços */}
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Enquanto isso, conheça o <span className="text-red-500">BIMPORTAL</span>
            </h2>
            <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto">
              Explore o nosso ecossistema completo de produtos e serviços em BIM.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.title}
                  href={s.href}
                  className="group bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-red-500/50 hover:bg-zinc-800/60 transition-all duration-300"
                  data-testid={`inscricao-servico-${s.href.replace('/', '')}`}
                >
                  <div className="w-11 h-11 rounded-lg bg-red-600/15 flex items-center justify-center mb-3 group-hover:bg-red-600/25 transition-colors">
                    <Icon className="w-6 h-6 text-red-500" />
                  </div>
                  <h3 className="text-white font-semibold mb-1 flex items-center gap-1">
                    {s.title}
                    <ArrowRight size={15} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-red-400" />
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
                </a>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <a
              href="https://bimportal.com.br"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
              data-testid="inscricao-home-link"
            >
              Voltar para a página inicial <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InscricaoRecebidaPage;
